import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

import {
  extractCandidateProfile,
  extractPdfText,
  getCandidateState,
  saveCandidateProfile,
} from "@/api/candidatePages";
import { invalidateCompanyWishlistCache } from "@/cache/companyWishlistCache";
import { useUserStore } from "@/stores/user";
import type { PagePhase } from "@/types/candidatePages";
import type { CandidateProfile } from "@/types/profile";

export const emptyProfile = (): CandidateProfile => ({
  address: "",
  application_password: "",
  city_or_town: "",
  country_of_residence: "",
  cv_filename: "",
  cv_text: "",
  degree_level: "",
  degree_subject: "",
  email: "",
  graduation_date: "",
  legal_name: "",
  phone: "",
  postal_code: "",
  preferred_location: "London",
  sponsorship_requirement: "",
  university: "",
  work_authorisation: "",
});

const requiredProfileFields: ReadonlyArray<keyof CandidateProfile> = [
  "legal_name",
  "email",
  "phone",
  "address",
  "city_or_town",
  "postal_code",
  "country_of_residence",
  "university",
  "degree_level",
  "degree_subject",
  "graduation_date",
  "work_authorisation",
  "sponsorship_requirement",
  "cv_filename",
  "preferred_location",
];

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof Error && caught.message ? caught.message : fallback;
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(new Error("The selected file could not be read."));
    reader.readAsDataURL(file);
  });
}

export const useProfileStore = defineStore("profile", () => {
  const userStore = useUserStore();
  const profile = reactive<CandidateProfile>(emptyProfile());
  const phase = ref<PagePhase>("idle");
  const error = ref("");
  const missing = ref<string[]>([]);
  const notices = ref<string[]>([]);
  const cvText = ref("");
  const selectedFile = ref<File | null>(null);
  const cvReadError = ref("");
  const readingCv = ref(false);
  const extracting = ref(false);
  const cvFileState = ref<"error" | "idle" | "loading" | "selected">("idle");
  const hasLoaded = ref(false);
  let activeController: AbortController | null = null;
  let fileController: AbortController | null = null;
  let fileGeneration = 0;
  let generation = 0;

  const busy = computed(() => phase.value === "loading" || phase.value === "updating");
  const canExtract = computed(() => !busy.value && !readingCv.value && Boolean(cvText.value.trim()));
  const statusLabel = computed(() => {
    if (phase.value === "loading") return "Loading profile";
    if (phase.value === "updating") return "Updating profile";
    if (phase.value === "error") return "Profile unavailable";
    if (missing.value.length) return `${missing.value.length} fields missing`;
    return hasLoaded.value ? "Profile complete" : "Waiting for CV";
  });

  function owns(controller: AbortController, requestGeneration: number): boolean {
    return activeController === controller && generation === requestGeneration && !controller.signal.aborted;
  }

  function begin(): { controller: AbortController; requestGeneration: number } {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    return { controller, requestGeneration: ++generation };
  }

  function applyProfile(nextProfile: CandidateProfile): void {
    Object.assign(profile, emptyProfile(), nextProfile);
    cvText.value = nextProfile.cv_text || cvText.value;
    missing.value = requiredProfileFields
      .filter((field) => !String(nextProfile[field] ?? "").trim())
      .map(String);
  }

  async function load(): Promise<void> {
    extracting.value = false;
    const { controller, requestGeneration } = begin();
    phase.value = hasLoaded.value ? "updating" : "loading";
    error.value = "";
    try {
      const state = await getCandidateState(controller.signal);
      if (!owns(controller, requestGeneration)) return;
      applyProfile(state.profile);
      hasLoaded.value = true;
      phase.value = "success";
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      error.value = errorMessage(caught, "The candidate profile could not be loaded.");
      phase.value = "error";
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function ownsFile(controller: AbortController, requestGeneration: number): boolean {
    return fileController === controller
      && fileGeneration === requestGeneration
      && !controller.signal.aborted;
  }

  async function selectFile(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    fileController?.abort();
    const controller = new AbortController();
    const requestGeneration = ++fileGeneration;
    fileController = controller;
    selectedFile.value = file;
    notices.value = [];
    cvReadError.value = "";
    if (!file) {
      cvFileState.value = "idle";
      fileController = null;
      return;
    }
    readingCv.value = true;
    cvFileState.value = "loading";
    cvText.value = "";
    try {
      const source = await textFromFile(file, controller.signal);
      if (!ownsFile(controller, requestGeneration)) return;
      if (!source.text.trim()) {
        throw new Error("The selected CV does not contain readable text.");
      }
      cvText.value = source.text;
      cvFileState.value = "selected";
    } catch (caught: unknown) {
      if (!ownsFile(controller, requestGeneration) || isAbortError(caught)) return;
      cvReadError.value = errorMessage(caught, "The selected CV could not be read.");
      cvFileState.value = "error";
    } finally {
      if (fileGeneration === requestGeneration) readingCv.value = false;
      if (fileController === controller) fileController = null;
    }
  }

  async function textFromFile(file: File, signal: AbortSignal): Promise<{ filename: string; text: string }> {
    const filename = file.name;
    const normalizedName = filename.toLowerCase();
    if (normalizedName.endsWith(".pdf")) {
      const result = await extractPdfText(await readAsBase64(file), signal);
      if (result.repaired) {
        const pages = result.repaired_pages ?? 0;
        notices.value.push(`Recovered fragmented words on ${pages} PDF page${pages === 1 ? "" : "s"}.`);
      }
      return { filename, text: result.text };
    }
    if (normalizedName.endsWith(".doc") || normalizedName.endsWith(".docx")) {
      throw new Error("DOC and DOCX extraction is not supported yet. Save the document as PDF or paste its text.");
    }
    return { filename, text: await file.text() };
  }

  async function extract(): Promise<void> {
    if (!canExtract.value) return;
    const { controller, requestGeneration } = begin();
    extracting.value = true;
    phase.value = hasLoaded.value ? "updating" : "loading";
    error.value = "";
    notices.value = [];
    try {
      const source = {
        filename: selectedFile.value?.name ?? "pasted-cv.txt",
        text: cvText.value,
      };
      const result = await extractCandidateProfile(source.filename, source.text, controller.signal);
      if (!owns(controller, requestGeneration)) return;
      applyProfile(result.profile);
      missing.value = result.missing;
      notices.value.push(...result.warnings);
      hasLoaded.value = true;
      phase.value = "success";
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      error.value = errorMessage(caught, "CV extraction failed.");
      phase.value = "error";
    } finally {
      if (generation === requestGeneration) extracting.value = false;
      if (activeController === controller) activeController = null;
    }
  }

  async function save(): Promise<void> {
    extracting.value = false;
    const { controller, requestGeneration } = begin();
    const ownerId = userStore.user?.id ?? null;
    phase.value = "updating";
    error.value = "";
    try {
      const result = await saveCandidateProfile(
        { ...profile, cv_text: cvText.value },
        controller.signal,
      );
      if (
        !owns(controller, requestGeneration) ||
        (userStore.user?.id ?? null) !== ownerId
      ) return;
      applyProfile(result.profile);
      missing.value = result.missing;
      hasLoaded.value = true;
      phase.value = "success";
      invalidateCompanyWishlistCache(ownerId);
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      error.value = errorMessage(caught, "The profile could not be saved.");
      phase.value = "error";
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function dispose(): void {
    generation += 1;
    fileGeneration += 1;
    activeController?.abort();
    fileController?.abort();
    activeController = null;
    fileController = null;
    readingCv.value = false;
    if (cvFileState.value === "loading") {
      if (cvText.value) {
        cvFileState.value = "selected";
      } else {
        selectedFile.value = null;
        cvFileState.value = "idle";
      }
    }
    extracting.value = false;
    if (phase.value === "updating") phase.value = hasLoaded.value ? "success" : "idle";
  }

  function reset(): void {
    generation += 1;
    fileGeneration += 1;
    activeController?.abort();
    fileController?.abort();
    activeController = null;
    fileController = null;
    Object.assign(profile, emptyProfile());
    phase.value = "idle";
    error.value = "";
    missing.value = [];
    notices.value = [];
    cvText.value = "";
    selectedFile.value = null;
    cvReadError.value = "";
    readingCv.value = false;
    extracting.value = false;
    cvFileState.value = "idle";
    hasLoaded.value = false;
  }

  return {
    busy,
    canExtract,
    cvFileState,
    cvReadError,
    cvText,
    dispose,
    error,
    extract,
    extracting,
    hasLoaded,
    load,
    missing,
    notices,
    phase,
    profile,
    readingCv,
    reset,
    save,
    selectFile,
    selectedFile,
    statusLabel,
  };
});
